import { COVER_CROP_MIX_TYPE } from "consts";

import type { CropYear, ScenarioMainCropOption } from "pages/app/scenario-analysis/types/scenario";

/** Returns a blank year with the given id — use everywhere a new empty year is needed. */
export const createEmptyYear = (id: string): CropYear => ({
  id,
  crop: "",
  coverCropId: COVER_CROP_MIX_TYPE.NONE,
  tillageId: null,
  residue: null,
  irrigation: false,
});

/** A year grows a cover crop; treats the interchangeable "nothing selected" encodings as none. */
export const growsCoverCrop = <T extends { coverCropId?: string | null }>(
  year: T
): year is T & { coverCropId: Exclude<NonNullable<T["coverCropId"]>, typeof COVER_CROP_MIX_TYPE.NONE> } =>
  Boolean(year.coverCropId) && year.coverCropId !== COVER_CROP_MIX_TYPE.NONE;

/** Whether a year row has any data entered (crop, cover crop, tillage, or residue) — as opposed to a fully blank row. */
export const cropYearHasAnyEntry = <T extends Pick<CropYear, "crop" | "coverCropId" | "tillageId" | "residue">>(year: T): boolean =>
  !!(year.crop || growsCoverCrop(year) || year.tillageId || year.residue != null);

/** Strips fully-blank rows from a crop-year list. Never prunes down to zero — a fully-blank list has nothing to strip to. */
export const pruneEmptyCropYears = (years: CropYear[]): CropYear[] => {
  const nonEmpty = years.filter(cropYearHasAnyEntry);
  return nonEmpty.length > 0 ? nonEmpty : years;
};

/** Residue lock for a crop: not enabled locks it to false, enabled and left by default locks it to true, otherwise it is free. */
export const getResidueLockState = (mainCropOptions: ScenarioMainCropOption[], cropId: string): { disabled: boolean; lockedValue?: boolean } => {
  const option = mainCropOptions.find((o) => o.value === cropId);
  if (!option) return { disabled: false };
  if (option.residueEnabled === false) return { disabled: true, lockedValue: false };
  if (option.residueEnabled === true && option.residueLeftByDefault === true) return { disabled: true, lockedValue: true };
  return { disabled: false };
};

export const isResidueLockedToLeftOnField = (mainCropOptions: ScenarioMainCropOption[], cropId: string): boolean =>
  getResidueLockState(mainCropOptions, cropId).lockedValue === true;

/** Corrects years whose residue contradicts their crop's lock in one pass; returns the same array when nothing changed. */
export const reconcileYearsResidueLocks = (years: CropYear[], mainCropOptions: ScenarioMainCropOption[]): CropYear[] => {
  let changed = false;
  const next = years.map((year) => {
    if (!year.crop) return year;
    const { lockedValue } = getResidueLockState(mainCropOptions, year.crop);
    if (lockedValue !== undefined && year.residue !== lockedValue) {
      changed = true;
      return { ...year, residue: lockedValue };
    }
    return year;
  });
  return changed ? next : years;
};
